// Prompt 0454 — Tab Datos extraido del AdminPuestoPanel monolitico 0453.
// Mantiene el form, preview-codigo en alta, autogenerado en edicion.

import { useState, useEffect } from 'react';
import {
  crearPuesto,
  actualizarPuesto,
  fetchOrganizaciones,
  fetchDepartamentos,
  fetchNivelesPuesto,
  fetchPreviewCodigoPuesto,
  request,
  type PuestoItem,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import {
  Field,
  ReadonlyField,
  SelectField,
  type SelectOption,
} from '../components/PuestoPanelFields';

export interface AdminPuestoPanelInput {
  id: string;
  titulo?: string;
  codigo?: string | null;
  nivelPuestoId?: string | null;
  organizacionId?: string | null;
  departamentoId?: string | null;
  rolFuncionalId?: string | null;
  fichaDocumentoId?: string | null;
}

interface DatosTabProps {
  puesto: AdminPuestoPanelInput | null;
  detalle: PuestoItem | null;
  onSaved: (nuevoPuestoId?: string) => void | Promise<void>;
}

export function DatosTab({ puesto, detalle, onSaved }: DatosTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;
  const isEdit = puesto !== null;

  const initialOrgId = detalle?.organizacion?.id ?? puesto?.organizacionId ?? '';
  const initialDeptId = detalle?.departamento?.id ?? puesto?.departamentoId ?? '';

  const [form, setForm] = useState<Record<string, string | undefined>>(() => ({
    titulo: detalle?.titulo ?? puesto?.titulo ?? '',
    nivelPuestoId:
      (detalle as unknown as { nivelPuestoId?: string })?.nivelPuestoId ??
      puesto?.nivelPuestoId ??
      '',
    rolFuncionalId: detalle?.rolFuncional?.id ?? puesto?.rolFuncionalId ?? '',
    organizacionId: initialOrgId,
    departamentoId: initialDeptId,
  }));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewCodigo, setPreviewCodigo] = useState<string | null>(null);

  const [orgs, setOrgs] = useState<SelectOption[]>([]);
  const [depts, setDepts] = useState<SelectOption[]>([]);
  const [niveles, setNiveles] = useState<SelectOption[]>([]);
  const [rolesFuncionales, setRolesFuncionales] = useState<Array<{ id: string; nombre: string }>>(
    [],
  );

  useEffect(() => {
    fetchOrganizaciones()
      .then((r) => setOrgs((r.data ?? []).map((o) => ({ value: o.id, label: o.nombre }))))
      .catch(() => {});
    fetchDepartamentos({ porPagina: '100' })
      .then((r) =>
        setDepts(
          (r.data ?? []).map((d) => ({ value: d.id, label: d.nombre, orgId: d.organizacionId })),
        ),
      )
      .catch(() => {});
    fetchNivelesPuesto()
      .then((r) => setNiveles((r.data ?? []).map((n) => ({ value: n.id, label: n.nombre }))))
      .catch(() => {});
    request<{ data: Array<{ id: string; nombre: string }> }>('/roles-funcionales')
      .then((r) => setRolesFuncionales(r.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) {
      fetchPreviewCodigoPuesto()
        .then((r) => setPreviewCodigo(r.proximoCodigo))
        .catch(() => setPreviewCodigo(null));
    }
  }, [isEdit]);

  const filteredDepts = form.organizacionId
    ? depts.filter((d) => d.orgId === form.organizacionId)
    : depts;

  const setField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError('');
  };

  const handleSave = async () => {
    if (!form.titulo?.trim()) {
      setError(panel.requiredTitulo ?? 'El titulo es obligatorio');
      return;
    }
    if (!form.nivelPuestoId) {
      setError(panel.requiredNivel ?? 'El nivel es obligatorio');
      return;
    }
    if (!form.organizacionId) {
      setError(panel.requiredOrg ?? 'La organizacion es obligatoria');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== undefined && v !== '') body[k] = v;
      }
      if (isEdit && puesto?.id) {
        await actualizarPuesto(puesto.id, body);
        await onSaved();
      } else {
        const res = await crearPuesto(body);
        await onSaved(res.data.id);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const codigoLabel =
    isEdit && (detalle?.codigo ?? puesto?.codigo)
      ? `${detalle?.codigo ?? puesto?.codigo} ${panel.codigoAutogenerado ?? '(autogenerado)'}`
      : previewCodigo
        ? `${previewCodigo} ${panel.codigoPreview ?? '(preview)'}`
        : (panel.codigoPendiente ?? '(se generara al guardar)');

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 16px',
          marginBottom: 4,
        }}
      >
        <Field
          label={panel.labelTitulo ?? 'Titulo'}
          required
          value={form.titulo}
          onChange={(v) => setField('titulo', v)}
        />
        <ReadonlyField label={panel.labelCodigo ?? 'Codigo'} value={codigoLabel} />
        <SelectField
          label={panel.labelNivel ?? 'Nivel'}
          required
          value={form.nivelPuestoId}
          options={niveles}
          onChange={(v) => setField('nivelPuestoId', v)}
        />
        <SelectField
          label={panel.labelRolFuncional ?? 'Rol funcional'}
          value={form.rolFuncionalId}
          options={rolesFuncionales.map((r) => ({ value: r.id, label: r.nombre }))}
          onChange={(v) => setField('rolFuncionalId', v)}
        />
        <SelectField
          label={panel.labelOrganizacion ?? 'Organizacion'}
          required
          value={form.organizacionId}
          options={orgs}
          onChange={(v) => {
            setForm((prev) => ({ ...prev, organizacionId: v, departamentoId: '' }));
            setError('');
          }}
        />
        <SelectField
          label={panel.labelDepartamento ?? 'Departamento'}
          value={form.departamentoId}
          options={filteredDepts}
          onChange={(v) => setField('departamentoId', v)}
        />
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: '#FEF2F2',
            color: '#DC2626',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 24px',
            background: '#2F5496',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            fontFamily: 'inherit',
          }}
        >
          {saving
            ? (panel.saving ?? 'Guardando...')
            : isEdit
              ? (panel.save ?? 'Guardar')
              : (panel.createPuesto ?? 'Crear puesto')}
        </button>
      </div>
    </div>
  );
}
